import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Flag } from '@mui/icons-material';
import apiService from '../services/apiService';

const RatingsPage = () => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRatings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getRatings();

      if (response.success) {
        setRatings(response.data);
      } else {
        setError(response.message || 'Failed to load ratings');
        loadMockData();
      }
    } catch (err) {
      console.error('Ratings error:', err);
      setError('Failed to load ratings');
      loadMockData();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  const loadMockData = () => {
    setRatings([
      {
        id: '1',
        rideId: 'RIDE001',
        reviewerName: 'Priya Sharma',
        targetName: 'Rajesh Kumar',
        type: 'driver',
        rating: 5,
        review: 'Excellent service, very professional driver!',
        createdAt: '2025-01-08 11:00 AM',
      },
      {
        id: '2',
        rideId: 'RIDE002',
        reviewerName: 'Suresh Reddy',
        targetName: 'Amit Kumar',
        type: 'passenger',
        rating: 4,
        review: 'Good passenger, polite and on time.',
        createdAt: '2025-01-08 12:00 PM',
      },
      {
        id: '3',
        rideId: 'RIDE003',
        reviewerName: 'Sneha Reddy',
        targetName: 'Vikram Singh',
        type: 'driver',
        rating: 2,
        review: 'Driver was rude and drove rashly.',
        createdAt: '2025-01-08 01:00 PM',
      },
    ]);
  };

  const handleFlag = async (id) => {
    if (!window.confirm('Flag this rating for review?')) {
      return;
    }

    try {
      const response = await apiService.flagRating(id, 'Inappropriate content');

      if (response.success) {
        alert('Rating flagged successfully');
        fetchRatings();
      } else {
        alert(response.message || 'Failed to flag rating');
      }
    } catch (err) {
      alert('Failed to flag rating');
    }
  };

  const getRatingStars = (rating) => {
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'success';
    if (rating >= 3) return 'warning';
    return 'error';
  };

  const columns = [
    {
      field: 'crn',
      headerName: 'CRN',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
          {params.row.crn || params.row.rideId?.substring(0, 8) + '...'}
        </Typography>
      ),
    },
    {
      field: 'reviewerName',
      headerName: 'Rated By',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.reviewerName || params.row.raterName || '-'}
        </Typography>
      ),
    },
    {
      field: 'targetName',
      headerName: 'Rated To',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.targetName || params.row.ratedName || '-'}
        </Typography>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.type || params.row.ratingType || '-'}
          size="small"
          color={params.row.type === 'driver' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Chip
            label={params.row.rating}
            color={getRatingColor(params.row.rating)}
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="caption">{getRatingStars(params.row.rating)}</Typography>
        </Box>
      ),
    },
    {
      field: 'review',
      headerName: 'Review',
      width: 300,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.review || params.row.comment || '-'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 160,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" color="error" onClick={() => handleFlag(params.row.id)}>
          <Flag fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Ratings & Reviews
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage passenger and driver ratings
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error} - Showing demo data
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={ratings}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default RatingsPage;
